import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  isSignInWithEmailLink,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signOut,
  signInWithEmailAndPassword,
  confirmPasswordReset,
  verifyPasswordResetCode,
  signInWithCustomToken,
} from 'firebase/auth';
import { myFirebaseAuth } from '../../firebase/config';
import axios from 'axios';
import { BASE_API_URL } from '../../utils/constants';
import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import {
  resetAuthRootState,
  updateLoginState,
  updateSendLoginEmailState,
} from '../globalSlices/authSlice';
import { DLog, openDillToast } from '../../utils/helpers';
import { resetBuyersRootState } from '../globalSlices/buyersSlice';
import { resetInvoicesRootState } from '../globalSlices/invoiceSlice';
import { resetJobsRootState } from '../globalSlices/jobsSlice';
import { resetLiensRootState } from '../globalSlices/liensSlice';
import { resetLienTemplatesRootState } from '../globalSlices/lienTemplatesSlice';
import { resetPaymentsRootState } from '../globalSlices/paymentsSlice';
import { resetSalesReceiptsRootState } from '../globalSlices/salesReceiptsSlice';
import { resetSuppliersRootState } from '../globalSlices/suppliersSlice';
import { resetBSubSuppliersRootState } from '../globalSlices/subSuppliersSlice';
import { AppUserUpdate, BuyerUserPermission, PASSWORD_POLICY_SUMMARY } from '@dill/dill-shared';
import { resetSupplierJobsRootState } from '../globalSlices/supplierJobsSlice';
import { resetManualUploadsRootState } from '../globalSlices/manualUploadSlice';
import { resetGenericsRootState } from '../globalSlices/genericSlice';
import { resetInforDistSXRootState } from '../globalSlices/inforDistSX';
import { resetNoticeTemplatesRootState } from '../globalSlices/noticeTemplatesSlice';
import { resetP21RootState } from '../globalSlices/p21Slice';
import { resetPreliminaryNoticesRootState } from '../globalSlices/preliminaryNoticesSlice';
import { resetRequestedSupplierJobsRootState } from '../globalSlices/requestJobInformationSlice';
import { resetSage100RootState } from '../globalSlices/sage100Slice';
import { resetSupplierBuyersRootState } from '../globalSlices/supplierBuyersSlice';
import { resetSupplierReleaseRootState } from '../globalSlices/supplierReleasesSlice';

export const logoutUser = createAsyncThunk('auth/logoutUser', async (data, thunkApi) => {
  try {
    // Get current user ID before logging out
    const userId = myFirebaseAuth.currentUser?.uid;

    thunkApi.dispatch(resetAuthRootState());
    thunkApi.dispatch(resetBuyersRootState());
    thunkApi.dispatch(resetInvoicesRootState());
    thunkApi.dispatch(resetJobsRootState());
    thunkApi.dispatch(resetLiensRootState());
    thunkApi.dispatch(resetLienTemplatesRootState());
    thunkApi.dispatch(resetPaymentsRootState());
    thunkApi.dispatch(resetSalesReceiptsRootState());
    thunkApi.dispatch(resetSuppliersRootState());
    thunkApi.dispatch(resetBSubSuppliersRootState());
    thunkApi.dispatch(resetSupplierJobsRootState());
    thunkApi.dispatch(resetManualUploadsRootState());
    thunkApi.dispatch(resetGenericsRootState());
    thunkApi.dispatch(resetInforDistSXRootState());
    thunkApi.dispatch(resetNoticeTemplatesRootState());
    thunkApi.dispatch(resetP21RootState());
    thunkApi.dispatch(resetPreliminaryNoticesRootState());
    thunkApi.dispatch(resetRequestedSupplierJobsRootState());
    thunkApi.dispatch(resetSage100RootState());
    thunkApi.dispatch(resetSupplierBuyersRootState());
    thunkApi.dispatch(resetSupplierReleaseRootState());

    console.log('user ---logged out');
    // No 'firebaseToken' key is ever written - the SDK keeps its tokens in IndexedDB, which
    // signOut() below clears. Server-side revocation is what actually ends the session.
    // localStorage.removeItem('referPath');
    // sessionStorage.removeItem('emailFromLink');

    // Clear SSO time on the backend (fire and forget)
    if (userId) {
      // axios.post(`${BASE_API_URL}/sso/clear-sso-time`, { userId }).catch((err) => {
      //   console.error('Failed to clear SSO time:', err);
      // });
    }

    // Revoke server-side before the local sign-out. signOut() below only clears this browser's
    // persistence - it makes no network call - so without this a refresh token copied off the
    // device keeps minting new ID tokens indefinitely. Best effort, and bounded: logging out must
    // never hang on a slow API, so this times out and falls through to the local sign-out. The
    // token also has to be read before signOut() clears it.
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (token) {
        await axios.post(
          `${BASE_API_URL}/authAPI/signOut`,
          {},
          { headers: { Authorization: 'Bearer ' + token }, timeout: 5000 }
        );
      }
    } catch (error) {
      console.error('Failed to revoke session on logout:', error);
    }

    await signOut(myFirebaseAuth);
    // thunkApi.dispatch(updateLoginState(''));
    // thunkApi.dispatch(getStripePublishableKey());

    return true;
  } catch (error: any) {
    return thunkApi.rejectWithValue(error.message);
  }
});

export const getStripePublishableKey = createAsyncThunk(
  'auth/getStripePublishableKey',
  async (data, thunkApi): Promise<any> => {
    try {
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/generics/stripeGenerics/getConfig`,
        {}
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);
export const getRutterPublicKey = createAsyncThunk(
  'auth/getRutterPublicKey',
  async ({ supplierId }: { supplierId: string }, thunkApi): Promise<any> => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        if (!token) {
          return null;
        }
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/rutter/config`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);
export const processRutterPublicToken = createAsyncThunk(
  'auth/processRutterPublicToken',
  async (
    { publicToken, supplierId }: { publicToken: string; supplierId: string },
    thunkApi
  ): Promise<any> => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        if (!token) {
          return null;
        }
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/rutter/processPublicToken`,
        { publicToken },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);
export const getUserDetails = createAsyncThunk('auth/getUserDetails', async (data, thunkApi) => {
  try {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    DLog(token);
    if (!token) {
      if (!token) {
        return null;
      }
    }
    const response = await axios.get<AppServerResponse>(BASE_API_URL + '/authAPI/user', {
      headers: {
        Authorization: 'Bearer ' + token,
      },
    });
    return response.data;
  } catch (error: any) {
    console.log(error);
    if (error?.response?.status === 401) {
      thunkApi.dispatch(logoutUser());
    }
    return thunkApi.rejectWithValue(error.message);
  }
});

export const getCurrentSupplier = createAsyncThunk(
  'auth/getCurrentSupplier',
  async ({ supplierId }: { supplierId: string }, thunkApi): Promise<any> => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/authAPI/user/supplier/${supplierId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const getUserLinkedSuppliers = createAsyncThunk(
  'auth/getUserLinkedSuppliers',
  async (_, thunkApi): Promise<any> => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/authAPI/user/suppliers`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const firebaseSendLoginEmailv1 = createAsyncThunk(
  'auth/firebaseLoginUser',
  async (data: { email: string }, thunkApi) => {
    try {
      const actionCodeSettings = {
        // URL you want to redirect back to. The domain (www.example.com) for this
        // URL must be in the authorized domains list in the Firebase Console.
        url: `${process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3000'}/login`,
        // url: 'http://localhost:3000/login',

        // This must be true.
        handleCodeInApp: true,
      };
      await sendSignInLinkToEmail(myFirebaseAuth, data.email, actionCodeSettings);
      sessionStorage.removeItem('emailFromLink');
      localStorage.setItem('emailForSignIn', data.email);
      console.log('success');
      thunkApi.dispatch(updateSendLoginEmailState(true));
      thunkApi.dispatch(getUserDetails());
      return { success: true };
    } catch (error: any) {
      console.log(error);
      console.log(error.message);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
export const firebaseSendLoginEmail = createAsyncThunk(
  'auth/firebaseLoginUser',
  async (data: { email: string; overrideCooldown?: boolean }, thunkApi) => {
    try {
      await axios.post<AppServerResponse>(BASE_API_URL + '/authAPI/sendEmailSignInLink', {
        email: data.email,
        ...(data.overrideCooldown ? { overrideCooldown: true } : {}),
      });

      // sessionStorage.removeItem('emailFromLink');
      localStorage.setItem('emailForSignIn', data.email);
      console.log('success');
      thunkApi.dispatch(updateSendLoginEmailState(true));
      thunkApi.dispatch(getUserDetails());
      return { success: true };
    } catch (error: any) {
      console.log(error);
      console.log(error.message);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
export const firebaseSendPasswordResetEmail = createAsyncThunk(
  'auth/firebaseSendPasswordResetEmail',
  async (data: { email: string }, thunkApi) => {
    try {
      await axios.post<AppServerResponse>(BASE_API_URL + '/authAPI/sendPasswordResetLink', {
        email: data.email,
      });

      sessionStorage.removeItem('emailFromLink');
      localStorage.setItem('emailForPasswordReset', data.email);
      console.log('success');
      // thunkApi.dispatch(updateSendLoginEmailState(true));
      // thunkApi.dispatch(getUserDetails());
      return { success: true };
    } catch (error: any) {
      console.log(error);
      console.log(error.message);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
// Firebase locks an account for a few minutes after repeated failed sign-ins, so the generic
// "Invalid Password" would send the user on retrying a password that is not the problem.
export const getLoginErrorMessage = (errorCode?: unknown) => {
  if (errorCode === 'auth/too-many-requests') {
    return 'Too many failed login attempts. Access to this account is temporarily disabled. Please wait a few minutes and try again, or use the password reset link above.';
  }
  if (errorCode === 'auth/user-disabled') {
    return 'This account has been disabled. Please contact support.';
  }
  if (errorCode === 'auth/network-request-failed') {
    return "Couldn't reach the server. Check your connection and try again.";
  }
  if (errorCode === 'auth/invalid-email') {
    return 'Enter a valid email address.';
  }
  return 'Invalid Password';
};

export const firebaseLoginEmailAndPassword = createAsyncThunk(
  'auth/firebaseLoginEmailAndPassword',
  async (data: { email: string; password: string }, thunkApi) => {
    try {
      await signInWithEmailAndPassword(myFirebaseAuth, data.email, data.password);

      thunkApi.dispatch(getUserDetails());
      return { success: true };
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error?.code || error?.message || 'error');
    }
  }
);

export const firebaseConfirmLoginUser = createAsyncThunk(
  'auth/firebaseConfirmLoginUser',
  async ({ url }: { url: string }, thunkApi) => {
    try {
      const emailForSignIn = localStorage.getItem('emailForSignIn');
      if (!emailForSignIn) {
        return null;
      }
      console.log(url);
      console.log(emailForSignIn);

      if (isSignInWithEmailLink(myFirebaseAuth, url)) {
        const response = await signInWithEmailLink(myFirebaseAuth, emailForSignIn, url);
        thunkApi.dispatch(updateLoginState(response.user.uid));
        console.log('"===="success');
        return { success: true, data: response.user.uid };
      }
      return null;
    } catch (error: any) {
      if (error?.message && error?.message === 'Firebase: Error (auth/invalid-action-code).') {
        openDillToast({
          type: 'ERROR',
          message:
            'Failed to verify your email. Make sure you click the latest verification email. This happens when you open multiple tabs and many verification emails are sent. Check your inbox and sign in from the latest email',
        });
      } else if (
        error?.message &&
        error?.message ===
          'Firebase: The email provided does not match the sign-in email address. (auth/invalid-email).'
      ) {
        openDillToast({
          type: 'ERROR',
          message:
            'Failed to verify your email. This link is not for this email address. You can resend the verification link for this email by entering your email below or open the verification link with the previous browser where the email for this link was set',
        });
      }
      console.log(error);
      console.log(error?.message);
      return thunkApi.rejectWithValue('error');
    }
  }
);

export const firebaseSignInWithCustomToken = createAsyncThunk(
  'auth/firebaseSignInWithCustomToken',
  async ({ token }: { token: string }, thunkApi) => {
    try {
      const response = await signInWithCustomToken(myFirebaseAuth, token);
      thunkApi.dispatch(updateLoginState(response.user.uid));
      console.log('"===="success');
      return { success: true, data: response.user.uid };
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      return thunkApi.rejectWithValue('error');
    }
  }
);

// Firebase invalidates every earlier password reset code once a newer one is generated for the
// same user, so a stale link fails with invalid/expired-action-code rather than a password problem.
export const STALE_PASSWORD_RESET_LINK_MESSAGE =
  "This password reset link is no longer valid. A newer reset email was sent, or this link has expired. Please open the most recent 'Reset your password' email, or request a new link below.";

export const isStalePasswordResetCodeError = (errorCode?: unknown) =>
  errorCode === 'auth/invalid-action-code' || errorCode === 'auth/expired-action-code';

// Failures that say nothing about the link itself. The code is still good, so keep it and let the
// user retry instead of sending them off for a new email — a new one would not help, and after
// too-many-requests it makes things worse.
export const isRetryablePasswordResetError = (errorCode?: unknown) =>
  errorCode === 'auth/network-request-failed' || errorCode === 'auth/too-many-requests';

export const getPasswordResetErrorMessage = (errorCode?: unknown) => {
  if (isStalePasswordResetCodeError(errorCode)) {
    return STALE_PASSWORD_RESET_LINK_MESSAGE;
  }
  if (errorCode === 'auth/network-request-failed') {
    return "Couldn't reach the server. Check your connection and try again — your reset link is still valid.";
  }
  if (errorCode === 'auth/too-many-requests') {
    return 'Too many attempts. Please wait a few minutes and try again — your reset link is still valid.';
  }
  if (errorCode === 'auth/weak-password') {
    return `Password is too weak. ${PASSWORD_POLICY_SUMMARY}`;
  }
  if (errorCode === 'auth/user-disabled' || errorCode === 'auth/user-not-found') {
    return "We couldn't reset the password for this account. Please contact support.";
  }
  return "Couldn't reset your password. Please request a new reset link and try again.";
};

export const firebaseConfirmPasswordReset = createAsyncThunk(
  'auth/firebaseConfirmPasswordResetUser',
  async ({ oobCode, newPassword }: { oobCode: string; newPassword: string }, thunkApi) => {
    try {
      await confirmPasswordReset(myFirebaseAuth, oobCode, newPassword);
      localStorage.removeItem('emailForPasswordReset');

      console.log('"===="success');
      return { success: true, data: {} };
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error?.code || error?.message || 'error');
    }
  }
);

// Validates the reset link up front so a stale one is reported before the user types a password.
// Also returns the account email, which the link itself is authoritative for.
export const firebaseVerifyPasswordResetCode = createAsyncThunk(
  'auth/firebaseVerifyPasswordResetCode',
  async ({ oobCode }: { oobCode: string }, thunkApi) => {
    try {
      const accountEmail = await verifyPasswordResetCode(myFirebaseAuth, oobCode);
      return { success: true, email: accountEmail };
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error?.code || error?.message || 'error');
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (data: { email: string }, thunkApi) => {
    const { email } = data;
    try {
      const actionCodeSettings = {
        // url: 'https://' + myFirebase.options.authDomain,
        // url: 'http://localhost:3000/login',
        url: `${process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3000'}/login`,
        // handleCodeInApp: true,
      };
      const res = await sendPasswordResetEmail(myFirebaseAuth, email, actionCodeSettings);
      console.log({ res, email });
      return true;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const checkUserExists = createAsyncThunk(
  'auth/checkUserExists',
  async (
    { email, thenLogIn = false }: { email: string; thenLogIn?: boolean },
    thunkApi
  ): Promise<any> => {
    try {
      const response = await axios.post<AppServerResponse>(BASE_API_URL + '/authAPI/userCheck', {
        email,
      });
      if (!response.data?.success) {
        thunkApi.dispatch(logoutUser());
      }
      if (
        response.data &&
        response.data?.success &&
        response?.data.data?.roles &&
        response?.data.data?.roles.length === 1 &&
        response?.data.data?.roles[0] === 'DRIVER'
      ) {
      } else if (response.data && response.data?.success && thenLogIn) {
        thunkApi.dispatch(firebaseSendLoginEmail({ email }));
      }
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const updateLoggedInUserPassword = createAsyncThunk(
  'auth/updateLoggedInUserPassword',
  async ({ email, password }: { email: string; password: string }, thunkApi): Promise<any> => {
    try {
      if (!myFirebaseAuth.currentUser) {
        return null;
      }
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        BASE_API_URL + '/authAPI/userPassword',
        {
          password,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      localStorage.removeItem('emailForSignIn');
      // const response = await updatePassword(myFirebaseAuth.currentUser, password);
      await thunkApi.dispatch(firebaseLoginEmailAndPassword({ email, password }));
      await thunkApi.dispatch(checkUserExists({ email }));
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const getPlaidLinkToken = createAsyncThunk(
  'auth/getPlaidLinkToken',
  async ({ isIncludeStatement = false }: { isIncludeStatement?: boolean }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/paymentsAPI/plaid/linkToken`,
        {
          params: { isIncludeStatement },
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      // response.data.;

      return response.data;
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
export const getPlaidLinkTokenNoAuth = createAsyncThunk(
  'auth/getPlaidLinkTokenNoAuth',
  async (
    { id, isIncludeStatement = false }: { id: string; isIncludeStatement?: boolean },
    thunkApi
  ) => {
    try {
      const response = await axios.get<AppServerResponse>(
        BASE_API_URL + `/generics/plaidGenerics/linkToken/${id}`,
        {
          params: { isIncludeStatement },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const additionalConsentPlaid = createAsyncThunk(
  'auth/additionalConsentPlaid',
  async ({ accessToken, products }: { accessToken: string; products: string[] }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/paymentsAPI/plaid/additionalConsent`,
        {
          accessToken,
          products,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getUserByBuyers = createAsyncThunk(
  'auth/getUserBuyers',
  async (data: { buyerId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/authAPI/userByProperty`,
        {
          params: { ...data },
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const referBuyerUsers = createAsyncThunk(
  'auth/referBuyerUsers',
  async (
    data: {
      supplierDomainId: string;
      referrerEmail: string;
      idReferrerAcceptSetup: boolean;
      buyerId?: string;
      referredUsers: {
        firstName: string;
        lastName: string;
        email: string;
        title?: string;
        permissions?: BuyerUserPermission[];
      }[];
    },
    thunkApi
  ): Promise<any> => {
    try {
      const response = await axios.post<AppServerResponse>(
        BASE_API_URL + '/authAPI/buyerUserReferral',
        {
          ...data,
        }
      );
      console.log(response.data);
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const referSubSupplierUsers = createAsyncThunk(
  'auth/referSubSupplierUsers',
  async (
    data: {
      buyerId: string;
      referrerEmail: string;
      idReferrerAcceptSetup: boolean;
      referredUsers: { firstName: string; lastName: string; email: string; title?: string }[];
    },
    thunkApi
  ): Promise<any> => {
    try {
      const response = await axios.post<AppServerResponse>(
        BASE_API_URL + '/authAPI/subSupplierUserReferral',
        {
          ...data,
        }
      );
      console.log(response.data);
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const updateSubUser = createAsyncThunk(
  'auth/updateSubUser',
  async (
    {
      buyerId,
      email,
      supplierDomainId,
      roles,
    }: {
      buyerId?: string;
      supplierDomainId?: string;
      email: string;
      roles?: string[];
    },
    thunkApi
  ): Promise<any> => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        BASE_API_URL + '/authAPI/updateSubUser',
        { buyerId, email, supplierDomainId, roles },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const checkUserIsSetupWithCompany = createAsyncThunk(
  'auth/checkUserIsSetupWithCompany',
  async (
    {
      companyId,
      email,
      supplierDomainId,
    }: {
      companyId?: string;
      email: string;
      supplierDomainId?: string;
    },
    thunkApi
  ): Promise<any> => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        BASE_API_URL + '/authAPI/checkUserIsSetupWithCompany',
        { email, companyId, supplierDomainId },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const updateUserDetails = createAsyncThunk(
  'auth/updateUserDetails',
  async (
    {
      userDetails,
      userId,
    }: {
      userDetails: AppUserUpdate;
      userId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<any>(
        `${BASE_API_URL}/authAPI/user/${userId}`,
        { ...userDetails },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateUserProfile',
  async (
    {
      profileDetails,
    }: {
      profileDetails: {
        firstName: string;
        lastName: string;
        title?: string;
        phoneNumber?: string;
      };
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<any>(
        `${BASE_API_URL}/authAPI/user/updateProfile`,
        { ...profileDetails },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const createUserSignature = createAsyncThunk(
  'auth/createUserSignatures',
  async (
    {
      signature,
      date,
    }: {
      signature: string;
      date: Date;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/authAPI/userSignatures`,
        { signature, date },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getUserSignatures = createAsyncThunk(
  'auth/getUserSignatures',
  async (data, thunkApi): Promise<any> => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/authAPI/userSignatures`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const deleteUserSignature = createAsyncThunk(
  'auth/deleteUserSignatures',
  async (
    {
      userSignatureId,
    }: {
      userSignatureId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.delete<AppServerResponse>(
        BASE_API_URL + `/authAPI/userSignatures/${userSignatureId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const newUserNotifyDill = createAsyncThunk(
  'auth/newUserNotifyDill',
  async (
    {
      email,
      domainId,
      customerAccountName,
      supplierName,
    }: { email: string; domainId?: string; customerAccountName?: string; supplierName?: string },
    thunkApi
  ) => {
    try {
      const response = await axios.post<any>(
        `${BASE_API_URL}/generics/notificationsGenerics/newUserNotifyDill`,
        { email, domainId, customerAccountName, supplierName },
        {}
      );
      return { success: true, data: response.data };
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getFirebaseUser = createAsyncThunk(
  'auth/getUserBuyers',
  async (data: { buyerId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/authAPI/userByProperty`,
        {
          params: { ...data },
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const createCreditAppFirebaseUser = createAsyncThunk(
  'auth/createCreditAppFirebaseUser',
  async (
    {
      email,
      domainId,
      firstName,
      lastName,
      templateId,
      applicationId,
      senderEmail,
      type,
      salesRepId,
      branchId,
    }: {
      email: string;
      domainId: string;
      firstName: string;
      lastName: string;
      templateId: string;
      applicationId?: string;
      senderEmail?: string;
      type?: string;
      salesRepId?: string;
      branchId?: string;
    },
    thunkApi
  ) => {
    try {
      localStorage.setItem('emailForSignIn', email);
      const response = await axios.post<AppServerResponse>(
        BASE_API_URL + '/authAPI/createCreditAppFirebaseUser',
        {
          email,
          domainId,
          firstName,
          lastName,
          templateId,
          ...(applicationId && { applicationId }),
          ...(senderEmail && { senderEmail }),
          ...(type && { type }),
          ...(salesRepId && { salesRepId }),
          ...(branchId && { branchId }),
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const checkFirebaseUser = createAsyncThunk(
  'auth/checkFirebaseUser',
  async ({ email, supplierName }: { email: string; supplierName?: string }, thunkApi) => {
    try {
      localStorage.setItem('emailForSignIn', email);
      const response = await axios.post<AppServerResponse>(
        BASE_API_URL + '/authAPI/checkFirebaseUser',
        {
          email,
          ...(supplierName ? { supplierName } : {}),
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const unverifyFirebaseUserAndSendLink = createAsyncThunk(
  'auth/unverifyFirebaseUserAndSendLink',
  async (
    {
      email,
      domainId,
      firstName,
      lastName,
      templateId,
      applicationId,
      senderEmail,
      type,
      salesRepId,
      branchId,
      skipUnverify,
      overrideCooldown,
    }: {
      email: string;
      domainId: string;
      firstName: string;
      lastName: string;
      templateId: string;
      applicationId?: string;
      senderEmail?: string;
      type?: string;
      salesRepId?: string;
      branchId?: string;
      skipUnverify?: boolean;
      overrideCooldown?: boolean;
    },
    thunkApi
  ) => {
    thunkApi.dispatch(logoutUser());
    localStorage.setItem('emailForSignIn', email);
    try {
      const response = await axios.post<AppServerResponse>(
        BASE_API_URL + '/authAPI/unverifyFirebaseUserAndSendLink',
        {
          email,
          domainId,
          firstName,
          lastName,
          templateId,
          ...(applicationId && { applicationId }),
          ...(senderEmail && { senderEmail }),
          ...(type && { type }),
          ...(salesRepId && { salesRepId }),
          ...(branchId && { branchId }),
          ...(skipUnverify && { skipUnverify }),
          ...(overrideCooldown && { overrideCooldown }),
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const getCompanyUsers = createAsyncThunk(
  'auth/getCompanyUsers',
  async (data: { supplierId?: string } | undefined, thunkApi) => {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return null;
    }
    try {
      const params = data?.supplierId ? { supplierId: data.supplierId } : {};
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/authAPI/getCompanyUsers`,
        {
          headers: { Authorization: 'Bearer ' + token },
          params,
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

// Same endpoint as getCompanyUsers (every AppUser linked to the supplier, unfiltered by role),
// but kept as a distinct thunk/state field so it doesn't collide with callers of `getCompanyUsers`
// / `getUsers` that rely on `state.auth.users` holding a role-filtered list (e.g. the credit
// application reviewer dropdown). Used to populate @mention pickers.
export const getMentionableUsers = createAsyncThunk(
  'auth/getMentionableUsers',
  async (data: { supplierId?: string } | undefined, thunkApi) => {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return null;
    }
    try {
      const params = data?.supplierId ? { supplierId: data.supplierId } : {};
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/authAPI/getCompanyUsers`,
        {
          headers: { Authorization: 'Bearer ' + token },
          params,
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const signInWithOtt = createAsyncThunk(
  'auth/signInWithOtt',
  async ({ ott }: { ott: string }, thunkApi) => {
    try {
      // 1. Exchange OTT for Firebase custom token
      const response = await axios.get<{
        success: boolean;
        customToken: string;
        user: { id: string; email: string; firstName: string; lastName: string; roles: string[] };
        error?: string;
      }>(`${BASE_API_URL}/sso/exchange-ott`, {
        params: { ott },
      });
      console.log({ data: response.data });
      if (!response.data.success || !response.data.customToken) {
        return thunkApi.rejectWithValue(response.data.error || 'Failed to exchange token');
      }

      // 2. Sign in with custom token
      const userCredential = await signInWithCustomToken(myFirebaseAuth, response.data.customToken);
      thunkApi.dispatch(updateLoginState(userCredential.user.uid));

      // 3. Get user details
      await thunkApi.dispatch(getUserDetails());

      return { success: true, user: response.data.user };
    } catch (error: any) {
      console.log(error);
      const errorMessage = error?.response?.data?.error || error?.message || 'SSO sign-in failed';
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const getUsers = createAsyncThunk(
  'auth/getUsers',
  async (
    {
      data,
    }: {
      data: AppUserUpdate;
    },
    thunkApi
  ): Promise<any> => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(`${BASE_API_URL}/authAPI/users`, {
        headers: {
          Authorization: 'Bearer ' + token,
        },
        params: {
          ...data,
        },
      });
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);
